import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import BrandMark from "@/components/BrandMark";
import { ArrowLeft, Loader2, Eye, EyeOff, MessageCircle } from "lucide-react";

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1 = request OTP, 2 = reset
  const [identifier, setIdentifier] = useState("");
  const [otp, setOtp] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const [channel, setChannel] = useState("");

  const requestOtp = async (e) => {
    e?.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { identifier });
      setChannel(data?.channel || "mobile");
      if (data?.mock_otp) {
        setOtp(data.mock_otp);
        toast.success(`WhatsApp/SMS not connected yet — code: ${data.mock_otp}`, { duration: 8000 });
      } else {
        toast.success("Verification code sent");
      }
      setStep(2);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Could not send the code. Try again shortly.");
    } finally {
      setBusy(false);
    }
  };

  const reset = async (e) => {
    e?.preventDefault();
    if (password.length < 6) return toast.error("Password must be at least 6 characters");
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { identifier, otp, password });
      toast.success("Password updated — please sign in");
      navigate("/login");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Reset failed. Check the code and try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="ep-aurora ep-grid-bg relative flex min-h-screen items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="ep-glass relative z-10 w-full max-w-md overflow-hidden rounded-3xl border border-slate-800 p-8 shadow-2xl sm:p-10"
      >
        <div className="mb-6 flex justify-center rounded-2xl bg-white py-3">
          <BrandMark size={40} />
        </div>
        <h1 className="text-2xl font-bold font-head text-white">Reset password</h1>
        <p className="mt-1 text-sm text-slate-400">
          {step === 1
            ? "We'll send a 6-digit code to the mobile number on your account via WhatsApp/SMS."
            : `Enter the code sent to your ${channel || "mobile"} and choose a new password.`}
        </p>

        {step === 1 ? (
          <form onSubmit={requestOtp} className="mt-6 space-y-4">
            <label className="block">
              <span className="mb-1.5 block text-xs font-medium text-slate-400">Email or mobile number</span>
              <input
                type="text" required value={identifier}
                onChange={(e) => {
                  const v = e.target.value;
                  setIdentifier(/^[\d\s+\-()]*$/.test(v) ? v.replace(/\D/g, "").slice(0, 10) : v);
                }}
                className="ep-input" placeholder="you@school.edu  ·  98765 43210"
                data-testid="forgot-identifier-input"
              />
              {/^\d{1,9}$/.test(identifier) && (
                <span className="mt-1 block text-[11px] text-amber-400">Mobile number must be 10 digits ({identifier.length}/10)</span>
              )}
            </label>
            <button type="submit" disabled={busy} className="ep-btn w-full" data-testid="forgot-send-btn">
              {busy ? <Loader2 size={18} className="animate-spin" /> : <><MessageCircle size={16} /> Send code</>}
            </button>
          </form>
        ) : (
          <form onSubmit={reset} className="mt-6 space-y-4">
            <label className="block">
              <span className="mb-1.5 block text-xs font-medium text-slate-400">6-digit code</span>
              <input
                inputMode="numeric" pattern="[0-9]*" maxLength={6} required value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                className="ep-input tracking-[0.4em] text-center text-lg" placeholder="••••••"
                data-testid="forgot-otp-input"
              />
            </label>
            <label className="block">
              <span className="mb-1.5 block text-xs font-medium text-slate-400">New password</span>
              <div className="relative">
                <input
                  type={showPw ? "text" : "password"} required value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="ep-input pr-11" placeholder="At least 6 characters"
                  data-testid="forgot-password-input"
                />
                <button
                  type="button" onClick={() => setShowPw((v) => !v)}
                  aria-label={showPw ? "Hide password" : "Show password"}
                  className="absolute inset-y-0 right-0 flex w-11 items-center justify-center text-slate-400 hover:text-white"
                >
                  {showPw ? <EyeOff size={17} /> : <Eye size={17} />}
                </button>
              </div>
            </label>
            <button type="submit" disabled={busy} className="ep-btn w-full" data-testid="forgot-reset-btn">
              {busy ? <Loader2 size={18} className="animate-spin" /> : "Update password"}
            </button>
            <button type="button" onClick={() => setStep(1)} className="w-full text-center text-xs text-slate-400 hover:text-white">
              Use a different number
            </button>
          </form>
        )}

        <Link to="/login" className="mt-6 inline-flex items-center gap-1.5 text-xs text-slate-400 hover:text-white">
          <ArrowLeft size={13} /> Back to sign in
        </Link>
      </motion.div>
    </div>
  );
}
